import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from "@mui/material";
import { formatCurrency } from "../../../../../utility/utils";
import { isNewPaymentValid, paymentDue } from './utils';
import { RootState } from '../../../../../state/store';
import { usePreferences } from '../../../../../utility/usePreferences';
import { SessionContext } from "../../../../../utility/useSession";
import { IBookingSalesAndPaymentsResult, PaymentTypes } from '../../../../../logic/api';
import { Trans } from 'react-i18next';
import { useReleaseManagement, ReleaseFlag } from '@/utility/feature-management';

interface ISubmitPaymentButton {
  applyPayment: () => void;
  partialPayment: boolean;
  saleData: IBookingSalesAndPaymentsResult;
}

const SubmitPaymentButton: React.FunctionComponent<ISubmitPaymentButton> = ({
  applyPayment,
  partialPayment,
  saleData
}) => {
  const [paymentButtonDisabled, setPaymentButtonDisabled] = useState(true);
  const [refundButtonDisabled, setRefundButtonDisabled] = useState(true);

  const { outstanding } = saleData;

  const accountNumber = useSelector((state: RootState) => state.payments.accountNumber);
  const amount = useSelector((state: RootState) => state.payments.amount);
  const authCode = useSelector((state: RootState) => state.payments.authCode);
  const description = useSelector((state: RootState) => state.payments.description);
  const paymentType = useSelector((state: RootState) => state.payments.paymentType);
  const reconciledSaleItems = useSelector((state: RootState) => state.payments.reconciledSaleItems);

  const session = useContext(SessionContext);
  const {
    isEligibleAccountPaymentMethod,
    isEligibleCardPaymentMethod,
    isEligibleRefundPaymentMethod
  } = usePreferences();

  const isMakePaymentDescriptionMandatoryForComp = useReleaseManagement(
    ReleaseFlag.releaseMakePaymentDescriptionMandatoryForComp
  );

  useEffect(() => {
    if (paymentType === PaymentTypes.refund) {
      isRefundButtonDisabled();
    } else {
      isPaymentButtonDisabled();
    }
  }, [
    authCode,
    accountNumber,
    amount,
    partialPayment,
    paymentType,
    reconciledSaleItems,
    description,
    isMakePaymentDescriptionMandatoryForComp
  ]);

  const isPaymentButtonDisabled = () => {
    const isRefund = paymentType === PaymentTypes.refund;

    if (
      isMakePaymentDescriptionMandatoryForComp &&
      (paymentType === PaymentTypes.compDiscount || paymentType === PaymentTypes.refund)
    ) {
      const hasDescription = description?.trim()?.length > 0;
      return setPaymentButtonDisabled(!hasDescription);
    }

    if (partialPayment) {
      return setPaymentButtonDisabled(
        !paymentType ||
        paymentDue(amount, outstanding) ||
        !isNewPaymentValid(isRefund, reconciledSaleItems) ||
        (
          (isEligibleCardPaymentMethod(paymentType) && !authCode) ||
          (isEligibleAccountPaymentMethod(paymentType) &&
            (!accountNumber || accountNumber.length < 3 || accountNumber.length > 99))
        )
      );
    }

    return setPaymentButtonDisabled(
      !paymentType ||
      paymentDue(amount, outstanding) ||
      (
        (isEligibleCardPaymentMethod(paymentType) && !authCode) ||
        (isEligibleAccountPaymentMethod(paymentType) &&
          (!accountNumber || accountNumber.length < 3 || accountNumber.length > 99))
      )
    );
  };

  const isRefundButtonDisabled = () => {
    if (
      isMakePaymentDescriptionMandatoryForComp &&
      paymentType === PaymentTypes.refund
    ) {
      const hasDescription = description?.trim()?.length > 0;
      return setRefundButtonDisabled(!amount || amount <= 0 || amount > saleData.paymentsTotal || !hasDescription)
    }

    if (partialPayment) {
      return setRefundButtonDisabled(!amount || amount <= 0 || amount > saleData.paymentsTotal);
    }

    return setRefundButtonDisabled(saleData.paymentsTotal <= 0);
  };

  const refundButtonStyle = {
    width: 280,
    marginLeft: -40,
    backgroundColor: !refundButtonDisabled ? '#E5005A' : undefined,
    color: !refundButtonDisabled ? '#fff' : undefined,
  };

  const paymentButtonStyle = {
    width: 280,
    marginLeft: -40,
    backgroundColor: !paymentButtonDisabled ? '#E5005A' : undefined,
    color: !paymentButtonDisabled ? '#fff' : undefined,
  };

  return (
    <div>
      {isEligibleRefundPaymentMethod(paymentType) ? (
        <Button
          disabled={refundButtonDisabled}
          onClick={applyPayment}
          style={refundButtonStyle}
          variant="contained"
          color="primary"
          data-testid="submit-payment-button"
        >
          <Trans i18nKey="payments.confirmRefund">
            Confirm refund of {{
              amount: partialPayment
                ? formatCurrency(amount, session)
                : formatCurrency(saleData.total - saleData.outstanding, session)
            }}
          </Trans>
        </Button>
      ) : (
        <Button
          disabled={paymentButtonDisabled}
          onClick={applyPayment}
          style={paymentButtonStyle}
          variant="contained"
          color="secondary"
          data-testid="submit-payment-button"
        >
          <Trans i18nKey="payments.confirmPayment">
            Confirm payment of {{ amount: formatCurrency(amount, session) }}
          </Trans>
        </Button>
      )}
    </div>
  );
};

export default SubmitPaymentButton;
